import type { TreeNode } from '@morph-mapper/types';
import { definePlugin } from '../../framework';
import type { FieldSuggestion, TransformStep } from '../../types';
import { isPlaceholder } from '../../utils/placeholders';

export type Notation = 'american' | 'european';
export type Rounding = 'ceil' | 'floor' | 'leave';

const hasNumberValidation = (validation: unknown): boolean =>
  (validation as { _def?: { typeName?: string } } | undefined)?._def?.typeName === 'ZodNumber';

const semanticsIndicateNumeric = (semantics: unknown): boolean =>
  typeof semantics === 'string' && /\bnumeric/i.test(semantics);

// Most numeric fields have number validation. These are the ones the
// default schemas also declare as plain text fields.
const KNOWN_NUMERIC_FIELD_KEYS = ['weight', 'tare', 'quantity', 'colli', 'temperature'];
const WEIGHT_FIELD_KEYS = ['weight', 'tare'];

const keyOf = (key: unknown) => (typeof key === 'string' ? key.trim().toLowerCase() : '');

// Date fields often have number validation too: they hold a timestamp, which
// is Date Handling's work, not a number to read from the text.
const isDateField = (key: unknown) => keyOf(key).includes('date');

export const isNumericIntentField = (entry: Pick<TreeNode, 'key' | 'validation' | 'semantics'>): boolean =>
  !isDateField(entry.key) &&
  (hasNumberValidation(entry.validation) ||
    semanticsIndicateNumeric(entry.semantics) ||
    KNOWN_NUMERIC_FIELD_KEYS.includes(keyOf(entry.key)));

/** A weight field: its unit is kg, and it is rounded as configured. */
export const isWeightField = (entry: Pick<TreeNode, 'key'>): boolean =>
  WEIGHT_FIELD_KEYS.includes(keyOf(entry.key));

// Units a number may carry in a document. Only tonnes are converted (to kg,
// for weight fields); the others are just removed. A word after a number
// that isn't a unit, like "HC" in "40HC", means the value isn't a number.
const TONNES = ['t', 'ton', 'tons', 'tonne', 'tonnes', 'mt'];
const UNITS = new Set([
  ...TONNES,
  'kg', 'kgs', 'kilo', 'kilos', 'kilogram', 'kilograms',
  '°c', '° c', 'c', '°', 'degrees',
  'pcs', 'pc', 'pieces', 'colli', 'ctns', 'cartons', 'pallets', 'plts', 'boxes',
  'cbm', 'm3', 'eur', '€', 'usd', '$',
]);

export type ReadNumber = { label: string; number: string; unit: string };

/**
 * The one number in a value, with an optional label before it (no digits,
 * not touching the number) and an optional known unit after it:
 * "Gross: 3,700 kg" → 3,700 / kg. Null when there is more than one number,
 * other digits, an unknown word after the number, or a date.
 */
export const readNumber = (raw: string): ReadNumber | null => {
  const match = raw.trim().match(/^([^\d]*?)([-+]?\d[\d.,]*)\s*([^\d]*)$/);
  if (!match) return null;
  const [, label, number, rest] = match;
  if (label !== '' && /[A-Za-z0-9]$/.test(label)) return null;
  if (/^\d{1,4}[.\-/]\d{1,2}[.\-/]\d{1,4}$/.test(number)) return null;
  const unit = rest.trim().toLowerCase();
  if (unit !== '' && !UNITS.has(unit)) return null;
  return { label, number, unit };
};

const toNotation = (value: unknown): Notation =>
  String(value).trim().toLowerCase() === 'european' ? 'european' : 'american';

const toRounding = (value: unknown): Rounding => {
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'floor') return 'floor';
  if (normalized === 'leave') return 'leave';
  return 'ceil';
};

const extractRawValue = (value: unknown): { prefix: string; raw: string } | null => {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(c\d+:\s*)(.*)$/);
  if (match) return { prefix: match[1], raw: match[2].trim() };
  return { prefix: '', raw: value.trim() };
};

const decimalIs = (separator: string): Notation => (separator === ',' ? 'european' : 'american');
const thousandsIs = (separator: string): Notation => (separator === '.' ? 'european' : 'american');

/**
 * The notation a number shows, or null when it could be either:
 * - with both "." and ",", the last one is the decimal separator
 *   ("21.630,000" is European, "1,234.50" American);
 * - a separator used more than once groups thousands ("1.234.567");
 * - a separator without exactly three digits after it is the decimal
 *   separator ("12,5", "8.25").
 * Only one separator with three digits after it ("3,700", "8.250") is
 * ambiguous.
 */
export const readNotation = (number: string): Notation | null => {
  const dot = number.lastIndexOf('.');
  const comma = number.lastIndexOf(',');
  if (dot >= 0 && comma >= 0) return comma > dot ? 'european' : 'american';
  const separator = dot >= 0 ? '.' : comma >= 0 ? ',' : null;
  if (separator === null) return null;
  const parts = number.split(separator);
  if (parts.length > 2) return thousandsIs(separator);
  if (parts[1].length !== 3) return decimalIs(separator);
  return null;
};

/** The notation a number shows, or the setting when it could be either. */
export const notationOf = (number: string, setting: Notation): Notation =>
  readNotation(number) ?? setting;

/**
 * The notation of numbers from one column: what the numbers that show it
 * agree on, else the setting. Null when they disagree.
 */
export const columnNotation = (numbers: string[], setting: Notation): Notation | null => {
  const shown = new Set(numbers.map(readNotation).filter((n): n is Notation => n !== null));
  if (shown.size > 1) return null;
  return [...shown][0] ?? setting;
};

const parseNumeric = (raw: string, notation: Notation): number | null => {
  const cleaned =
    notation === 'european'
      ? raw.replace(/\./g, '').replace(/,/g, '.')
      : raw.replace(/,/g, '');

  if (!cleaned.trim() || Number.isNaN(Number(cleaned))) return null;

  return Number(cleaned);
};

const applyRounding = (value: number, rounding: Rounding): number => {
  if (rounding === 'ceil') return Math.ceil(value);
  if (rounding === 'floor') return Math.floor(value);
  return value;
};

const isWrappedSnippet = (snippet: unknown): snippet is [unknown[]] =>
  Array.isArray(snippet) &&
  Array.isArray(snippet[0]) &&
  snippet[0].length > 0;

const buildNotationSnippet = (source: unknown, notation: Notation) => {
  if (notation === 'european') {
    return {
      '$utils.regexp.replace': [
        { '$utils.regexp.replace': [source, ['[.]', 'g'], ''] },
        ['[,]', 'g'],
        '.',
      ],
    };
  }

  return {
    '$utils.regexp.replace': [source, ['[,]', 'g'], ''],
  };
};

const buildRoundingSnippet = (source: unknown, rounding: Rounding) => {
  if (rounding === 'ceil') return { '$math.ceil': source };
  if (rounding === 'floor') return { '$math.floor': source };
  return source;
};

// Removes a label before the number and a unit after it, so only the number
// is left to parse: "Gross: 3,700 kg" → "3,700". The sign stays.
const buildStripTextSnippet = (source: unknown) => ({
  '$utils.regexp.replace': [
    { '$utils.regexp.replace': [source, ['^[^0-9+-]*', ''], ''] },
    ['[^0-9]*$', ''],
    '',
  ],
});

export type NumberSnippetOptions = {
  /** The value has a label or unit around the number to remove first. */
  stripText?: boolean;
  /** Multiplies the number, e.g. 1000 for tonnes to kg. */
  factor?: number;
};

export const buildNumberSnippet = (
  originalSnippet: unknown,
  notation: Notation,
  rounding: Rounding,
  { stripText = false, factor = 1 }: NumberSnippetOptions = {},
) => {
  const source = stripText ? buildStripTextSnippet(originalSnippet) : originalSnippet;
  const parsed = { '$math.parseFloat': buildNotationSnippet(source, notation) };
  const scaled = factor === 1 ? parsed : { '$math.multiply': [parsed, factor] };
  return buildRoundingSnippet(scaled, rounding);
};

/**
 * The number in every row, skipping empty rows and placeholders. Null when
 * a row holds something else: one snippet can't read all of them.
 */
const readRows = (rows: unknown[]): ReadNumber[] | null => {
  const read: ReadNumber[] = [];
  for (const row of rows) {
    if (row === undefined || row === null || isPlaceholder(row)) continue;
    const raw = extractRawValue(String(row))?.raw ?? '';
    if (raw === '') continue;
    const number = readNumber(raw);
    if (number === null) return null;
    read.push(number);
  }
  return read;
};

type NumberPlan = { notation: Notation; factor: number; stripText: boolean };

/**
 * How to read the numbers one snippet will see. Null when they need
 * different handling: mixed notations, or kg and tonnes in one column.
 */
const planFor = (numbers: ReadNumber[], setting: Notation, weight: boolean): NumberPlan | null => {
  const notation = columnNotation(numbers.map((n) => n.number), setting);
  if (notation === null) return null;
  // Only a weight is known to be in kg, so only a weight converts tonnes.
  const factors = new Set(numbers.map((n) => (weight && TONNES.includes(n.unit) ? 1000 : 1)));
  if (factors.size > 1) return null;
  return {
    notation,
    factor: [...factors][0],
    stripText: numbers.some((n) => n.label !== '' || n.unit !== ''),
  };
};

export const normalizeNumberSuggestion = (
  suggestion: Pick<FieldSuggestion, 'value' | 'snippet' | 'iterator'>,
  notation: Notation,
  rounding: Rounding,
  { weight = false, rows }: { weight?: boolean; rows?: unknown[] } = {},
): { value: string; snippet: unknown } | null => {
  if (suggestion.iterator === true) return null;

  const snippet = suggestion.snippet;
  const wrapped = isWrappedSnippet(snippet);
  if (snippet === undefined || snippet === null || snippet === '') return null;

  const extracted = extractRawValue(suggestion.value);
  if (extracted === null) return null;

  const read = readNumber(extracted.raw);
  if (read === null) return null;
  const rowNumbers = rows === undefined ? [] : readRows(rows);
  if (rowNumbers === null) return null;
  const plan = planFor([read, ...rowNumbers], notation, weight);
  if (plan === null) return null;
  const numericValue = parseNumeric(read.number, plan.notation);
  if (numericValue === null) return null;

  const options = { stripText: plan.stripText, factor: plan.factor };
  const build = (source: unknown) => buildNumberSnippet(source, plan.notation, rounding, options);

  return {
    value: extracted.prefix + String(applyRounding(numericValue * plan.factor, rounding)),
    snippet: wrapped ? [[build((snippet as unknown[][])[0][0])]] : build(snippet),
  };
};

export const numberStep = (notation: Notation, rounding: Rounding): TransformStep => ({
  name: 'parseNumber',
  stage: 'number',
  mode: 'replace',
  apply: (state, { entry, suggestion, rows }) => {
    if (!isNumericIntentField(entry)) return [];
    // Rounding is meant for weights; prices, temperatures and the like keep
    // their decimals.
    const weight = isWeightField(entry);
    // The rows are what the original snippet renders to, so they only
    // describe this state while no earlier step (like a split) changed it.
    const sameSnippet = state.snippet === suggestion.snippet;
    const normalized = normalizeNumberSuggestion(
      {
        value: state.value,
        snippet: state.snippet as FieldSuggestion['snippet'],
        iterator: suggestion.iterator,
      },
      notation,
      weight ? rounding : 'leave',
      { weight, rows: sameSnippet ? rows : undefined },
    );
    if (!normalized) return [];
    // A text field that already holds exactly this number gains nothing from
    // the formula ("20" stays "20"). A number field keeps it: parseFloat is
    // what turns the text into a number.
    const unchanged = typeof state.value === 'string' && normalized.value === state.value.trim();
    if (unchanged && !hasNumberValidation(entry.validation)) return [];
    return [normalized];
  },
});

export const numberHandling = definePlugin<{}>({
  name: 'Parse Number',
  version: '0.2.0',
  description:
    'Replaces Parse to Float: turns a number in the text into a real number, with the chosen notation and rounding.',
  initialState: {},
  settings: {
    notation: {
      name: 'Notation',
      type: 'select',
      default: 'american',
      options: [
        { value: 'american', label: 'American' },
        { value: 'european', label: 'European' },
      ],
    },
    rounding: {
      name: 'Rounding',
      type: 'select',
      default: 'ceil',
      options: [
        { value: 'ceil', label: 'Round up' },
        { value: 'floor', label: 'Round down' },
        { value: 'leave', label: 'Leave' },
      ],
    },
  },

  transformSteps: ({ settings }) => [
    numberStep(toNotation(settings['notation']), toRounding(settings['rounding'])),
  ],
});
